import React, { useState, useEffect } from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    PointElement,
    Title,
    Tooltip,
    Legend,
    ArcElement,
} from 'chart.js';
import { Bar, Line, Doughnut } from 'react-chartjs-2';
import { BarChart3, TrendingUp, PieChart } from 'lucide-react';
import { userService } from '../../services/user';

// Registrar componentes de Chart.js
ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    PointElement,
    Title,
    Tooltip,
    Legend,
    ArcElement
);

const StatisticsCharts = ({ stats }) => {
    const [chartData, setChartData] = useState(null);
    const [loading, setLoading] = useState(false);

    // Cargar datos de gráficos desde el backend
    useEffect(() => {
        const fetchChartData = async () => {
            setLoading(true);
            try {
                const data = await userService.getChartData();
                setChartData(data);
            } catch (error) {
                console.error('Error fetching chart data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchChartData();
    }, []);

    // Datos para gráfico de barras - Estados de solicitudes
    const solicitudesBarData = {
        labels: ['Pendientes', 'En Proceso', 'Completadas'],
        datasets: [
            {
                label: 'Solicitudes',
                data: [
                    stats?.solicitudes?.pendientes || 0,
                    stats?.solicitudes?.en_proceso || 0,
                    (stats?.solicitudes?.total || 0) - (stats?.solicitudes?.pendientes || 0) - (stats?.solicitudes?.en_proceso || 0)
                ],
                backgroundColor: [
                    'rgba(249, 115, 22, 0.8)',
                    'rgba(59, 130, 246, 0.8)',
                    'rgba(34, 197, 94, 0.8)',
                ],
                borderColor: [
                    'rgba(249, 115, 22, 1)',
                    'rgba(59, 130, 246, 1)',
                    'rgba(34, 197, 94, 1)',
                ],
                borderWidth: 1,
            },
        ],
    };

    // Datos para gráfico de dona - Activos
    const activosDonutData = {
        labels: ['En Servicio', 'Fuera de Servicio'],
        datasets: [
            {
                data: [
                    stats?.activos?.en_servicio || 0,
                    stats?.activos?.fuera_servicio || 0,
                ],
                backgroundColor: [
                    'rgba(34, 197, 94, 0.8)',
                    'rgba(239, 68, 68, 0.8)',
                ],
                borderColor: [
                    'rgba(34, 197, 94, 1)',
                    'rgba(239, 68, 68, 1)',
                ],
                borderWidth: 2,
            },
        ],
    };

    // Datos para gráfico de línea - Actividad semanal real
    const actividadLineData = {
        labels: chartData?.actividad_semanal?.map(item => item.dia) || ['Lun', 'Mar', 'Mie', 'Jue', 'Vie', 'Sab', 'Dom'],
        datasets: [
            {
                label: 'Solicitudes',
                data: chartData?.actividad_semanal?.map(item => item.solicitudes) || [0, 0, 0, 0, 0, 0, 0],
                borderColor: 'rgba(59, 130, 246, 1)',
                backgroundColor: 'rgba(59, 130, 246, 0.1)',
                tension: 0.4,
                fill: true,
            },
            {
                label: 'Órdenes de Trabajo',
                data: chartData?.actividad_semanal?.map(item => item.ordenes) || [0, 0, 0, 0, 0, 0, 0],
                borderColor: 'rgba(147, 51, 234, 1)',
                backgroundColor: 'rgba(147, 51, 234, 0.1)',
                tension: 0.4,
                fill: true,
            },
        ],
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top',
            },
        },
        scales: {
            y: {
                beginAtZero: true,
                ticks: {
                    stepSize: 1,
                },
            },
        },
    };

    const donutOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'bottom',
            },
        },
    };

    const lineOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top',
            },
        },
        scales: {
            y: {
                beginAtZero: true,
                ticks: {
                    stepSize: 1,
                },
            },
        },
    };

    if (loading) {
        return (
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6 mb-8">
                {[...Array(3)].map((_, i) => (
                    <div key={i} className="bg-white rounded-lg shadow-md p-6">
                        <div className="animate-pulse">
                            <div className="h-4 bg-gray-200 rounded w-1/2 mb-4"></div>
                            <div className="h-64 bg-gray-200 rounded"></div>
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6 mb-8">
            {/* Gráfico de Barras - Estado de Solicitudes */}
            <div className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-center mb-4">
                    <BarChart3 className="h-5 w-5 text-blue-600 mr-2" />
                    <h3 className="text-lg font-semibold text-gray-900">Estado de Solicitudes</h3>
                </div>
                <div className="h-64">
                    <Bar data={solicitudesBarData} options={chartOptions} />
                </div>
            </div>

            {/* Gráfico de Dona - Estado de Activos */}
            <div className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-center mb-4">
                    <PieChart className="h-5 w-5 text-green-600 mr-2" />
                    <h3 className="text-lg font-semibold text-gray-900">Estado de Activos</h3>
                </div>
                <div className="h-64">
                    <Doughnut data={activosDonutData} options={donutOptions} />
                </div>
                <div className="mt-4 text-center">
                    <p className="text-sm text-gray-600">
                        Total: {(stats?.activos?.total || 0)} activos
                    </p>
                </div>
            </div>

            {/* Gráfico de Línea - Actividad Semanal */}
            <div className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center">
                        <TrendingUp className="h-5 w-5 text-purple-600 mr-2" />
                        <h3 className="text-lg font-semibold text-gray-900">Actividad Semanal</h3>
                    </div>
                    {loading && <div className="text-sm text-gray-500">Cargando...</div>}
                </div>
                <div className="h-64">
                    <Line data={actividadLineData} options={lineOptions} />
                </div>
                <div className="mt-4 text-center">
                    <p className="text-sm text-gray-600">
                        Datos de los últimos 7 días
                    </p>
                </div>
            </div>
        </div>
    );
};

export default StatisticsCharts;